// Contracts of the /api/v1 dossier import API (backend/app/schemas/imports.py).

export type DocumentStatus = "queued" | "processing" | "ready" | "needs_review" | "failed";
export type PeriodRole = "N" | "N-1" | "other" | "unknown";
export type DocumentType = "QRT" | "SFCR" | "RSR" | "ACTUARIAL_REPORT" | "FINANCIAL_REPORT" | "ORG_CHART"
  | "GROUP_TEMPLATE" | "ORSA" | "POLICY" | "SUPPORTING_DOCUMENT" | "UNKNOWN";

export const DOCUMENT_TYPES: DocumentType[] = ["QRT", "SFCR", "RSR", "ACTUARIAL_REPORT", "FINANCIAL_REPORT",
  "ORG_CHART", "GROUP_TEMPLATE", "ORSA", "POLICY", "SUPPORTING_DOCUMENT", "UNKNOWN"];

export interface Dossier {
  id: string;
  name: string;
  entity_name: string;
  reporting_year: number;
  report_type: "SFCR" | "RSR";
  created_at: string;
  created_by: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
}

export interface JobStage {
  name: string;
  status: "running" | "succeeded" | "failed" | "skipped";
  started_at?: string;
  finished_at?: string;
  duration_ms?: number;
  detail?: string;
}

export interface Job {
  id: string;
  document_id: string;
  version_id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  stage: string | null;
  stages: JobStage[];
  attempt: number;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  document_name?: string;
}

export interface DocumentVersion {
  id: string;
  version_number: number;
  original_filename: string;
  file_format: "pdf" | "docx" | "xlsx" | "pptx";
  size_bytes: number;
  sha256: string;
  status: DocumentStatus;
  uploaded_at: string;
  uploaded_by: string;
  page_count: number | null;
  error_code: string | null;
  error_message: string | null;
  detected_type: DocumentType | null;
  classification_confidence: number | null;
  classification_signals: string[];
  detected_year: number | null;
  year_confidence: number | null;
  detected_entity: string | null;
  warnings: string[];
  processed_at: string | null;
}

export interface ReviewReason {
  kind: "classification" | "period" | "entity" | "entity_unknown" | "ocr" | "qrt" | string;
  message: string;
}

export interface DossierDocument {
  id: string;
  dossier_id: string;
  display_name: string;
  status: DocumentStatus;
  document_type: DocumentType | null;
  reporting_year: number | null;
  period_role: PeriodRole | null;
  entity_name: string | null;
  metadata_source: "detected" | "user";
  /** The unit of its QRT cells as the user set it, over the stated one. */
  qrt_unit: "EUR" | "kEUR" | "MEUR" | null;
  review_reasons: ReviewReason[];
  created_at: string;
  updated_at: string;
  current_version: DocumentVersion | null;
  latest_job: Job | null;
  used_by: string[];
}

export interface QrtTemplateSummary {
  id: string;
  template_code: string;
  title: string | null;
  sheet: string | null;
  pages: number[];
  reporting_year: number | null;
  fact_count: number;
  warnings: string[];
}

export interface DocumentDetail extends DossierDocument {
  versions: DocumentVersion[];
  counts: { sections: number; tables: number; qrt_facts: number; evidence: number };
  qrt_templates: QrtTemplateSummary[];
}

export interface DocumentDataPoint {
  id: string;
  indicator: string;
  label: string;
  stated_year: "N" | "N-1";
  period_role: PeriodRole;
  value: number;
  unit: string;
  raw_value: string;
  page: number | null;
  found_in_section: string | null;
  evidence: string;
  confidence: "eleve" | "moyen" | "faible";
  method: string;
  sections: string[];
  chapters: string[];
}

export interface ImportSummary {
  dossier_id: string;
  reporting_year: number;
  entity_name: string;
  documents: { total: number; ready: number; needs_review: number; processing: number; failed: number };
  available_sources: Record<string, { label: string; available: boolean; pending: boolean }>;
}

export type ReadinessStatus = "READY" | "PARTIALLY_READY" | "NOT_READY";

export interface Requirement {
  key: string;
  label: string;
  required: boolean;
  state: "satisfied" | "pending" | "missing";
  /** "extracted": met by data points import located in a narrative document, not by a QRT. */
  via?: "documents" | "extracted" | null;
  documents: { id: string; name: string; status: DocumentStatus }[];
}

export interface SectionReadiness {
  code: string;
  title: string;
  status: ReadinessStatus;
  requirements: Requirement[];
}

export interface InputReadiness extends ImportSummary {
  documents_in_progress: { id: string; name: string; status: DocumentStatus }[];
  chapters: { code: string; title: string; sections: SectionReadiness[] }[];
}

export interface AuditEventItem {
  id: string;
  actor: string;
  action: string;
  target_type: string;
  target_id: string;
  details: Record<string, unknown>;
  created_at: string;
}

export interface SourceSection {
  id: string;
  code: string;
  title: string;
  level: number;
  pages: number[];
  content: string;
  truncated: boolean;
  tables: { id: string; title: string | null; location: Record<string, unknown>; rows: (string | number | null)[][] }[];
}

export interface SectionSources {
  code: string;
  previous_reports: {
    document: { id: string; name: string; document_type: DocumentType; reporting_year: number | null;
      period_role: PeriodRole | null; status: DocumentStatus };
    sections: SourceSection[];
  }[];
  qrt_templates: {
    id: string; template_code: string; title: string | null; fact_count: number; reporting_year: number | null;
    pages: number[]; sheet: string | null; warnings: number;
    document: { id: string; name: string; period_role: PeriodRole | null; status: DocumentStatus };
  }[];
}

export interface QrtFactItem {
  id: string;
  template_code: string;
  row_code: string | null;
  column_code: string;
  record: number | null;
  row_label: string | null;
  column_label: string | null;
  value: string | null;
  value_text: string | null;
  unit: string | null;
  unit_assumed: boolean;
  reporting_year: number | null;
  sheet: string | null;
  cell_ref: string | null;
  page: number | null;
}

export interface ChapterRun {
  id: string;
  chapter_code: string;
  status: "queued" | "running" | "succeeded" | "failed";
  summary: Record<string, { block_status: string; findings: number; gaps: number; error: string | null }>;
  error_code: string | null;
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface PassageItem {
  id: string;
  document_id: string | null;
  document_name: string;
  title: string;
  page: number | null;
  period_role: string | null;
  location: string | null;
  excerpt?: string;
  text?: string;
}

export interface FactItem {
  indicator: string;
  value: number;
  unit: string | null;
  year: string;
  source_doc_id: string;
  page: number | null;
  evidence: string;
  confidence: "eleve" | "moyen" | "faible";
  raw_value: string | null;
  location: string | null;
  extraction_method: string | null;
}

export interface SectionInputs {
  code: string;
  kind: "quantitative" | "narrative" | "absence" | "chapter_lead";
  facts: Record<string, FactItem>;
  passages: PassageItem[];
  notes: string[];
  missing: string[];
  required: string[];
  optional_prior_year: string[];
  can_draft: boolean;
  ai_configured: boolean;
}

export interface BlockCitation {
  sourceId: string;
  kind?: "fact" | "passage";
  title?: string | null;
  docId: string | null;
  page: number | null;
  location: string | null;
  indicator: string | null;
  year: string | null;
  quote: string;
}

export interface DerivedFigureItem {
  indicator: string;
  value: number;
  unit: string;
  year: string;
  formula: string;
  inputs: FactItem[];
}

export interface AuditFindingItem {
  block_code: string;
  check: string;
  severity: "blocking" | "warning";
  message: string;
  article_id: string | null;
  remediation: string | null;
}

export interface BlockResult {
  block_id: string;
  code: string;
  status: "empty" | "running" | "produced" | "audit_ko" | "escalated" | "validated";
  draft_text: string | null;
  citations: BlockCitation[];
  gaps: string[];
  audit_findings: AuditFindingItem[];
  derived_figures: DerivedFigureItem[];
  generation: { provider?: string; model?: string; deployment?: string; prompt_version?: string;
    usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | null };
  error: string | null;
}

export interface Generation {
  id: string;
  section_code: string;
  status: "queued" | "running" | "succeeded" | "failed";
  block_status: BlockResult["status"] | null;
  error_code: string | null;
  error_message: string | null;
  provider: string | null;
  model: string | null;
  prompt_version: string | null;
  duration_ms: number | null;
  requested_by: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  inputs?: { facts: Record<string, FactItem>; notes: string[]; missing: string[]; passages?: PassageItem[] };
  result?: BlockResult;
}

export type AuditKind = "coherence" | "regulatory" | "vintage";
export type AuditResult = "ok" | "ko" | "review" | "na";

export interface AuditAppearance {
  section: string | null;
  kind: "text" | "source";
  value: string;
  location?: string;
  document_id?: string | null;
  matches: boolean;
}

export interface AuditRow {
  id: string;
  audit: AuditKind;
  section: string | null;
  result: AuditResult;
  comment: string;
  from_check: boolean;
  label?: string;
  indicator?: string | null;
  year?: string;
  sections?: string[];
  appearances?: AuditAppearance[];
  reference?: string;
  item?: string;
  automated?: boolean;
  excerpt?: string | null;
  unit?: string;
  current?: number | null;
  published?: number | null;
  difference?: number | null;
  difference_pct?: number | null;
  unit_assumed?: boolean;
}

export interface AuditCounts { total: number; ok: number; ko: number; review: number; na: number }

export interface ReportAudits {
  dossier_id: string;
  reporting_year: number;
  language: "fr" | "en";
  generated_at: string;
  sections_drafted: number;
  summary: Record<AuditKind | "all", AuditCounts>;
  audits: Record<AuditKind, AuditRow[]>;
  vintage: { published_report: string | null; previous_qrt: string | null };
}

export type ReviewStatus = "none" | "ai_draft" | "edited" | "changes_requested" | "approved";

export interface ReviewDecision {
  id: string;
  decision: "approved" | "changes_requested";
  content_kind: "generation" | "revision";
  content_id: string;
  comment: string | null;
  open_findings: AuditFindingItem[];
  actor: string;
  created_at: string;
}

export interface SectionContent {
  kind: "generation" | "revision";
  id: string;
  text: string;
  citations: BlockCitation[];
  findings: AuditFindingItem[];
  table: FigureTableData | null;
  tables?: SFCRTable[];
  generation_id: string;
  version_number: number | null;
  created_at: string;
  created_by: string;
}

export type ReviewHistoryItem =
  | { type: "revision"; id: string; version_number: number; actor: string; created_at: string; findings: number; blocking: number }
  | ({ type: "review" } & ReviewDecision);

export interface SectionReviewState {
  code: string;
  status: ReviewStatus;
  current: SectionContent | null;
  decision: ReviewDecision | null;
  last_approved: ReviewDecision | null;
  approved_is_current: boolean;
  /** The earlier version has identical text, figures and source provenance. */
  carried_from: ReviewDecision | null;
  /** Documents added or reprocessed after the last agent run that feed this section. */
  new_inputs: { id: string; name: string; processed_at: string | null }[];
  history: ReviewHistoryItem[];
  stale_sources?: string[];
}

export interface ReviewSummary {
  items: Record<string, ReviewStatus>;
  counts: Partial<Record<ReviewStatus, number>>;
  reviewable: number;
  /** Sections whose documents changed since their last agent run. */
  new_inputs: string[];
}

export interface ReportBlock {
  code: string;
  title: string;
  level: 1 | 2;
  kind: string;
  chapter: string;
  status: ReviewStatus;
  text: string | null;
  citations: BlockCitation[];
  table: FigureTableData | null;
  tables?: SFCRTable[];
  content_kind: "generation" | "revision" | null;
  version_number: number | null;
  blocking: number;
  approved_by: string | null;
  approved_at: string | null;
}

export interface ReportView {
  dossier_id: string;
  entity_name: string;
  report_type: string;
  reporting_year: number;
  language: "fr" | "en";
  counts: { total: number; drafted: number; approved: number; changes_requested: number };
  blocks: ReportBlock[];
}

export interface FigureTableRow {
  indicator: string;
  unit: string;
  n: number | null;
  n1: number | null;
  delta: number | null;
  delta_unit: string | null;
  variation: number | null;
  variation_unit: string | null;
  datapoint: string | null;
  formula: string | null;
  source_n: string | null;
  source_n1: string | null;
}

export interface FigureTableData { rows: FigureTableRow[]; has_prior: boolean; has_variation: boolean }

export interface SFCRTableColumn {
  key: string;
  label: string;
  type?: "text" | "number" | "currency" | "percentage" | "percentage_point";
}

export interface SFCRTableRow {
  indicator: string;
  indicatorKey?: string;
  unit?: string | null;
  previousValue?: string | number | null;
  currentValue?: string | number | null;
  variation?: string | number | null;
  variationType?: "percent" | "percentage_point" | "absolute" | null;
  sourceRefs?: string[];
  validationStatus?: string;
  provenance?: { current?: Record<string, unknown> | null; previous?: Record<string, unknown> | null; gaap?: Record<string, unknown> | null };
  provenanceLabels?: Record<string, string>;
  siiValue?: number | null;
  gaapValue?: number | null;
  bridgeValue?: number | null;
}

export interface SFCRTable {
  id: string;
  title?: string | null;
  columns: SFCRTableColumn[];
  rows: SFCRTableRow[];
  sourceRefs?: string[];
}

export interface PlanBlock {
  code: string;
  title: string;
  kind: string;
  chapter: string;
  level: 1 | 2;
  previous: { section_id: string; code: string; title: string; pages: number[]; document_id: string; document: string } | null;
  mapping: "same" | "renumbered" | "title_differs" | "absent" | "none";
}

export interface PlanItem {
  id: string;
  type: "conflict" | "proposal";
  severity: "high" | "medium" | "low";
  code: string | null;
  message: string;
  hint: string;
  decision: { decision: "accepted" | "rejected"; comment: string | null; actor: string; at: string } | null;
}

export interface ReportPlanView {
  dossier_id: string;
  status: "draft" | "validated" | "outdated";
  has_previous_report: boolean;
  previous_reports: string[];
  counts: { blocks: number; kinds: number; conflicts: number; proposals: number; pending: number };
  blocks: PlanBlock[];
  items: PlanItem[];
  validated_by: string | null;
  validated_at: string | null;
}

export type RaciRole = "R" | "A" | "C" | "I";

export interface Contributor { id: string; name: string; email: string; role: string | null; department: string | null; active: boolean }

export interface TeamView {
  contributors: Contributor[];
  scopes: { code: string; title: string }[];
  raci: Record<string, Record<RaciRole, string[]>>;
}

export interface ContributorRequestItem {
  id: string;
  section_code: string;
  gap: string | null;
  question: string;
  status: "open" | "answered" | "cancelled";
  answer: string | null;
  answered_at: string | null;
  created_by: string;
  created_at: string;
  contributor: Contributor | null;
}

export interface PublicContribution {
  entity_name: string;
  report_type: string;
  reporting_year: number;
  section_code: string;
  section_title: string;
  question: string;
  gap: string | null;
  status: "open" | "answered" | "cancelled";
  answer: string | null;
  contributor_name: string | null;
  asked_by: string;
  asked_at: string;
}

export interface Referential {
  articles: ({ id: string; code: string; title: string; description: string; criteria: string; default: string; sections: string[];
    automated: boolean } & VersionTracking)[];
  sections: ({ code: string; title: string; kind: string; chapter: string; instructions: string; default: string; required_facts: string[];
    optional_facts: string[]; prior_facts: string[]; templates: string[]; document_types: string[]; checks: string[] } & VersionTracking)[];
  identities: { check: string; section: string; reference: string; item: string }[];
  drafting: { mode: string; deployment: string; configured: boolean; prompt_version: string; rules: Record<string, string>; guardrails: string[] };
}

export interface CurrentUser {
  mode: "off" | "local";
  user: { id: string | null; email: string; name: string; role: "preparer" | "reviewer" | "approver" | "admin" };
}

export interface PlatformUser { id: string; email: string; name: string; role: CurrentUser["user"]["role"]; active: boolean }

export interface ReferentialVersionItem {
  id: string;
  kind: "article" | "section";
  key: string;
  field: string;
  version_number: number;
  value: string;
  previous_value: string;
  reason: string;
  status: "proposed" | "approved" | "rejected";
  proposed_by: string;
  proposed_at: string;
  decided_by: string | null;
  decided_at: string | null;
  decision_comment: string | null;
}

export interface VersionTracking { version: number; pending: ReferentialVersionItem | null; changed_by: string | null; changed_at: string | null }

export interface SectionComparison {
  code: string;
  previous: { document: { id: string; name: string; reporting_year: number | null };
    sections: { code: string; title: string; pages: number[] }[]; text: string } | null;
  current: { kind: "generation" | "revision"; id: string; text: string; version_number: number | null; status: ReviewStatus } | null;
}

export interface ScheduleItem {
  code: string;
  title: string;
  level: 1 | 2;
  chapter: string;
  owner: { id: string; name: string; email: string } | null;
  due_date: string | null;
  due_source: "section" | "chapter" | "filing" | null;
  own_date: string | null;
  days_left: number | null;
  status: ReviewStatus;
  late: boolean;
  due_soon: boolean;
}

export interface ScheduleView {
  dossier_id: string;
  today: string;
  filing_deadline: string | null;
  counts: { total: number; approved: number; late: number; due_soon: number; without_owner: number; without_date: number };
  items: ScheduleItem[];
}

export interface DatapointItem {
  indicator: string;
  label: string;
  datapoint: string;
  template: string | null;
  unit: string;
  n: number | null;
  n1: number | null;
  delta: number | null;
  delta_unit: string | null;
  variation: number | null;
  source_n: string | null;
  document_n: string | null;
  document_n_id: string | null;
  source_n1: string | null;
  document_n1: string | null;
  document_n1_id: string | null;
  confidence: "eleve" | "moyen" | "faible" | null;
  unit_assumed: boolean;
  /** "qrt": a reported QRT cell; "text": located verbatim in a narrative document at import. */
  method_n: "qrt" | "text" | null;
  method_n1: "qrt" | "text" | null;
  used_by: string[];
  chapters: string[];
}

export interface DatapointsView {
  dossier_id: string;
  notes: string[];
  counts: { indicators: number; found_n: number; found_n1: number; with_change: number; from_text: number };
  items: DatapointItem[];
  missing: string[];
}
